import type { AppShareActions, SetAppState } from "../app-store-contract";
import {
  restoreShareEquationDocuments,
  restoreShareWorkspace,
} from "../../share-workspace";
import type { EquationEvaluationCoordinator } from "@src/features/equation-editor/evaluation/evaluation-coordinator";
import { isEvaluationAbort } from "@src/features/equation-editor/evaluation/evaluator-contract";
import { createInitialAppState } from "../app-state";

export function createShareActions(
  set: SetAppState,
  evaluationCoordinator: EquationEvaluationCoordinator,
): AppShareActions {
  const createId = (): string => crypto.randomUUID();
  let loadGeneration = 0;
  let cancelPendingLoad: (() => void) | null = null;
  const cancelLoad = (): void => {
    loadGeneration += 1;
    cancelPendingLoad?.();
    cancelPendingLoad = null;
  };
  return {
    resetApp: () => {
      cancelLoad();
      evaluationCoordinator.cancelAll();
      set(() => ({
        ...createInitialAppState(createId),
      }));
    },
    cancelShareLoad: cancelLoad,
    loadShareDocument: async (document) => {
      cancelLoad();
      evaluationCoordinator.cancelAll();
      const generation = loadGeneration;
      const equations = restoreShareEquationDocuments(document, createId);
      const request2 = evaluationCoordinator.begin(2);
      const request3 = evaluationCoordinator.begin(3);
      const cancel = (): void => {
        request2.cancel();
        request3.cancel();
      };
      cancelPendingLoad = cancel;
      try {
        const [evaluation2, evaluation3] = await Promise.all([
          request2.evaluate(equations[2]),
          request3.evaluate(equations[3]),
        ]);
        if (
          generation !== loadGeneration ||
          !request2.isCurrent() ||
          !request3.isCurrent()
        )
          return;
        const loaded = restoreShareWorkspace(document, {
          2: { document: equations[2], result: evaluation2 },
          3: { document: equations[3], result: evaluation3 },
        });
        set(() => ({
          ...loaded,
        }));
      } catch (error) {
        if (!isEvaluationAbort(error)) throw error;
      } finally {
        if (cancelPendingLoad === cancel) cancelPendingLoad = null;
        cancel();
      }
    },
  };
}
